import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {Subscription} from 'rxjs';

@Component({
  selector: 'gatewee-interest-fine',
  templateUrl: './gatewee-interest-fine.component.html',
  styleUrls: ['./gatewee-interest-fine.component.css'],
  standalone: false,
})
export class GateweeInterestFineComponent
  implements OnInit, OnChanges, OnDestroy
{
  formArray!: FormArray<FormGroup>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formSaved: any[] = [];
  saved: boolean[] = [];

  @Input() formInterestFine!: FormGroup;

  private sub?: Subscription;

  whiteListSplit: {label: string; value: string | number}[] = [];
  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.formArray = this.fb.array<FormGroup>([]);
  }

  private setRequiredValidators(): void {
    const interest = this.formInterestFine.get('interestMonthlyPercent');
    const fine = this.formInterestFine.get('finePercent');

    interest?.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(100),
      this.maxPercentValidator(100),
    ]);

    fine?.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(100),
      this.maxPercentValidator(100),
    ]);

    interest?.updateValueAndValidity({emitEvent: false});
    fine?.updateValueAndValidity({emitEvent: false});
  }

  private clearRequiredValidators(): void {
    const interest = this.formInterestFine.get('interestMonthlyPercent');
    const fine = this.formInterestFine.get('finePercent');

    interest?.clearValidators();
    fine?.clearValidators();

    interest?.updateValueAndValidity({emitEvent: false});
    fine?.updateValueAndValidity({emitEvent: false});
  }

  private maxPercentValidator(max: number) {
    return (control: AbstractControl) => {
      const value = Number(control.value);
      if (Number.isNaN(value)) return null;
      return value > max ? {notMoreThan100: true} : null;
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['formInterestFine']?.currentValue) return;

    this.sub?.unsubscribe();
    this.hydrateFromForm();

    this.sub = this.formInterestFine.valueChanges.subscribe(() => {
      if (this.formArray?.length === 0) this.hydrateFromForm();
      else {
        if (this.saved[0]) {
          this.formSaved[0] = this.formInterestFine.getRawValue();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private hasValue(v: unknown) {
    return v !== null && v !== undefined && v !== '';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private hasInterestFineData(v: any): boolean {
    return (
      this.hasValue(v?.interestMonthlyPercent) || this.hasValue(v?.finePercent)
    );
  }

  private hydrateFromForm(): void {
    if (!this.formInterestFine) return;

    const value = this.formInterestFine.getRawValue();
    if (!this.hasInterestFineData(value)) return;

    if (this.formArray?.length === 0) {
      this.formArray.push(this.formInterestFine);

      this.formSaved = [value];
      this.saved = [true];
      return;
    }

    this.formSaved[0] = value;
    this.saved[0] = true;
  }

  addInterestForm(): void {
    if (this.formArray.length > 0) return;

    this.formInterestFine.reset(
      {interestMonthlyPercent: '', finePercent: ''},
      {emitEvent: true},
    );

    this.setRequiredValidators();

    this.formArray.push(this.formInterestFine);
    this.formSaved = [this.formInterestFine.getRawValue()];
    this.saved = [false];
  }

  saveSingle(i: number): void {
    const form = this.formArray.at(i);
    if (!form || form.invalid) return;

    const rawValue = form.getRawValue();
    this.formSaved[i] = rawValue;
    this.saved[i] = true;

    this.formInterestFine.patchValue(rawValue, {emitEvent: true});
    this.formInterestFine.markAsDirty();
    this.formInterestFine.updateValueAndValidity({emitEvent: true});
  }

  removeSingle(): void {
    if (this.formArray?.length === 0) return;

    this.formArray.removeAt(0);
    this.formSaved = [];
    this.saved = [];

    this.formInterestFine.reset(
      {interestMonthlyPercent: '', finePercent: ''},
      {emitEvent: true},
    );

    this.clearRequiredValidators();

    this.formInterestFine.markAsPristine();
    this.formInterestFine.updateValueAndValidity({emitEvent: true});
  }

  edit(i: number): void {
    if (!this.saved[i]) return;

    this.saved[i] = false;
    this.formArray.at(i).patchValue(this.formSaved[i], {emitEvent: false});

    this.setRequiredValidators();
  }
}
